/**
 * 2D satellite map for placing/visualizing orientation stops.
 *
 * Replaces the mockup's 3D first-person scene. Renders each stop as a numbered
 * marker (from `visible_orientation_steps.location`) plus an ordered polyline of
 * the walk path. Click a marker to select; in edit mode (onPlace/onMove given)
 * click the map to drop the selected stop and drag a marker to reposition.
 *
 * Bare react-leaflet (no MUI), ESRI World Imagery default with street/topo
 * toggle — mirrors the TurboTwin webclient TwinMapPage tile set. The basemap
 * choice is owned by the editor (the LayersMenu in the shared top bar) and
 * arrives as a prop.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Marker, Polyline, Tooltip as LeafletTooltip, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
// Moving-twin markers and geospatial shapes come from the shared viewer
// package's `./map/leaflet` entry, which a CI boundary test keeps free of
// turbotwin-sdk (React + Leaflet + markercluster only). The vendored copy has
// no built `dist/`, so vite.config + tsconfig alias the entry to its source.
// `extractGeoObjects`/`GeoObject` live in the sibling pure-data `./map` entry.
import {
  MoversLayer,
  GeoShapesLayer,
  ClusteredMarkersLayer,
  TurboTwinMap,
  type MoverEntry,
  type TwinMarkerEntry,
} from 'turbotwin-viewer-react/map/leaflet';
import { TILE_LAYERS, type GeoObject, type TileLayerId } from 'turbotwin-viewer-react/map';
import { useUnits } from '@/hooks/useUnits';
import { Icon } from '../Icon';
import { PortalIcons } from '../portalIcons';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster';
import 'leaflet.markercluster/dist/MarkerCluster.css';

export type StepMapStop = { stepId: string; name: string; lat: number; lon: number; el: number; index: number };
export type TwinMarker = {
  twinId: string;
  /** Resolved display name (TWIN-DISPLAY-NAME chain); absent = show the id. */
  label?: string;
  lat: number;
  lon: number;
  /** Model color (per-dtmi), used as the marker fill/ring. */
  color: string;
  /** Model 3D-asset thumbnail; when present the marker renders it inside a
   *  colored ring (webclient map language). */
  thumbnailUrl?: string;
};
/** A live-moving vehicle twin (today: trucks, matched by dtmi), drawn as a
 *  small truck glyph that tracks its latest telemetry position and points in
 *  its heading. Unlike {@link TwinMarker} these are never clustered and update
 *  position imperatively each telemetry tick, so a parked truck still shows and
 *  a moving one slides across the map. */
export type MoverMarker = {
  twinId: string;
  /** Resolved display name; absent = show the id. */
  label?: string;
  lat: number;
  lon: number;
  /** Model color (per-dtmi), used as the truck fill. */
  color: string;
  /** Compass bearing in degrees (0 = north, clockwise) — the glyph points this
   *  way. Undefined leaves it pointing north (no heading known yet). */
  heading?: number;
};
/** One geospatial twin property (a `complex_property` Geospatial value),
 *  drawn as a map overlay: `positions` is an array of lines/rings/point-sets
 *  of [lat, lon]. Colored by the owning twin's model. */
/** One twin's Geospatial shapes for the map, decoded by the shared
 *  `extractGeoObjects`. Grouped per twin so each `GeoShapesLayer` carries that
 *  twin's model colour and tooltip identity. */
export type GeoGroup = {
  twinId: string;
  /** Owning twin's resolved display name; absent = show the id. */
  twinLabel?: string;
  /** Stroke/fill colour (the owning model's colour). */
  color: string;
  geoObjects: GeoObject[];
};

type StepMapProps = {
  stops: StepMapStop[];
  selectedStepId: string | null;
  onSelect: (stepId: string) => void;
  /** Armed click (Add-stop placement or a Location pick): the next map click
   *  lands here. Absent = plain clicks do nothing to locations. */
  onPlace?: (lat: number, lon: number) => void;
  /** Edit mode: drag a marker to reposition its stop. */
  onMove?: (stepId: string, lat: number, lon: number) => void;
  /** Placing mode: the next map click creates a new stop. Shows a crosshair. */
  placing?: boolean;
  /** Geo-located twins, shown as clustered context markers (like the 3D view). */
  twins?: TwinMarker[];
  /** Live-moving vehicle twins (trucks), drawn un-clustered and tracking their
   *  telemetry position + heading. */
  movers?: MoverMarker[];
  /** Geospatial twin properties, drawn as colored overlays (webclient map style). */
  geo?: GeoGroup[];
  /** The selected geo command's location — shown as a pulsing target marker so
   *  the author sees where the command points. */
  commandGeo?: { lat: number; lon: number } | null;
  /** Active basemap (owned by the editor's LayersMenu). */
  basemap: BasemapKey;
};

// Highest zoom; past a base layer's own deepest tiles the shared map enlarges
// them (the shared tileLayers own the per-provider native-zoom caps).
const MAX_ZOOM = 19;
/** The editor's basemap choice is a shared tile-layer id (satellite/street/terrain). */
export type BasemapKey = TileLayerId;

/** Basemap picker: compact layers-icon button with a dropdown, rendered in the
 *  editor's top control bar (2D mode only). */
export function LayersMenu({ value, onChange }: { value: BasemapKey; onChange: (k: BasemapKey) => void }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="oe-layers">
      <button
        className={'oe-layers-btn' + (open ? ' on' : '')}
        aria-label="Background layers"
        title="Background layers"
        onClick={() => setOpen((o) => !o)}
      >
        <Icon of={PortalIcons.action.layers} />
      </button>
      {open && (
        <div className="oe-layers-menu" role="menu">
          {TILE_LAYERS.map((l) => (
            <button
              key={l.id}
              role="menuitem"
              className={l.id === value ? 'on' : ''}
              onClick={() => { onChange(l.id); setOpen(false); }}
            >
              {l.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function pinIcon(label: string, selected: boolean): L.DivIcon {
  return L.divIcon({
    className: '',
    html: `<div class="oe-step-pin${selected ? ' sel' : ''}">${label}</div>`,
    iconSize: [22, 22],
    iconAnchor: [11, 11],
  });
}

/** A moving twin for MoversLayer, carrying the owning model's colour through so
 *  `moverColor` can read it back (the layer passes each entry to colorOf whole). */
type TruckEntry = MoverEntry & { color: string };

/** A mover's marker colours for the shared MoversLayer: the owning model's
 *  colour fills the hexagon, with a white stroke so the border — and the heading
 *  nose, which takes the stroke — stay legible over satellite imagery (both the
 *  model colour, as the old glyph was, left the nose blending into the body).
 *  Module-level so the prop reference stays stable. */
const moverColor = (m: TruckEntry) => ({ fill: m.color, stroke: '#ffffff' });

/** A geo-located context twin for the shared `ClusteredMarkersLayer`, carrying
 *  the owning model's colour through so `twinColor` can read it back (the layer
 *  passes each entry to colorOf whole). */
type ContextTwinEntry = TwinMarkerEntry & { color: string };

/** A context twin's marker colours: the owning model's colour fills the shape
 *  and rings it (as the old coloured dot's border did). Module-level so the prop
 *  reference stays stable. */
const twinColor = (t: ContextTwinEntry) => ({ fill: t.color, stroke: t.color });

/** Fit the map to all stops on mount (covers switching 3D → 2D, which
 *  remounts the map) and whenever the set of stop ids changes. Deferred a
 *  beat so the freshly-mounted container has its real size — fitting against
 *  a zero-size container framed the wrong view. */
function FitBounds({ stops }: { stops: StepMapStop[] }) {
  const map = useMap();
  const key = stops.map((s) => s.stepId).join('|');
  useEffect(() => {
    if (!stops.length) return;
    const t = setTimeout(() => {
      map.invalidateSize();
      if (stops.length === 1) {
        map.setView([stops[0].lat, stops[0].lon], 18);
      } else {
        const bounds = L.latLngBounds(stops.map((s) => [s.lat, s.lon] as [number, number]));
        map.fitBounds(bounds, { padding: [40, 40], maxZoom: MAX_ZOOM });
      }
    }, 60);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, map]);
  return null;
}

/** Geospatial property overlays, drawn by the shared `GeoShapesLayer`
 *  (polygons, lines with a wide invisible hit buffer, points) — one layer per
 *  twin so each carries its model colour and a tooltip of property name +
 *  owning twin. `interactive={!placing}` goes non-interactive while placing so a
 *  click reaches the map and a stop can be dropped anywhere, a line included
 *  (the layer redraws its shapes when it flips; `highlight` still lights one). */
function GeoOverlayLayer({ geo, placing }: { geo: GeoGroup[]; placing?: boolean }) {
  return (
    <>
      {geo.map((g) => (
        <GeoShapesLayer
          key={g.twinId}
          geoObjects={g.geoObjects}
          color={g.color}
          interactive={!placing}
          renderTooltip={(obj) => (
            <div className="oe-geo-tip">
              <strong>{obj.name}</strong>
              <span>{g.twinLabel && g.twinLabel !== g.twinId ? `${g.twinLabel} (${g.twinId})` : g.twinId}</span>
            </div>
          )}
        />
      ))}
    </>
  );
}

function ClickToPlace({ onPlace }: { onPlace?: (lat: number, lon: number) => void }) {
  useMapEvents({
    click(e) {
      if (onPlace) onPlace(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
}

const COMMAND_GEO_ICON = L.divIcon({
  className: '',
  html: '<div class="oe-cmd-loc"></div>',
  iconSize: [20, 20],
  iconAnchor: [10, 10],
});

export function StepMap({ stops, selectedStepId, onSelect, onPlace, onMove, placing, twins, movers, geo, commandGeo, basemap }: StepMapProps) {
  const { fmtElevation } = useUnits();

  const center = useMemo<[number, number]>(() => {
    if (stops.length) return [stops[0].lat, stops[0].lon];
    return [0, 0];
  }, [stops]);

  const path = useMemo(() => stops.map((s) => [s.lat, s.lon] as [number, number]), [stops]);

  // Adapt the editor's MoverMarker inputs to the shared MoversLayer's MoverEntry
  // shape (id + position), carrying the model colour through for `moverColor`.
  // MoversLayer matches markers by id and moves them in place each tick, turns
  // the heading nose the short way, and never clusters a moving twin.
  const moverEntries = useMemo<TruckEntry[]>(
    () =>
      (movers ?? []).map((m) => ({
        id: m.twinId,
        position: { latitude: m.lat, longitude: m.lon },
        heading: m.heading ?? null,
        label: m.label ?? null,
        color: m.color,
      })),
    [movers],
  );

  // Adapt context TwinMarkers to the shared ClusteredMarkersLayer's entry shape
  // (id + position + displayName/modelName/locationMode), carrying the model
  // colour through for `twinColor`. Static context twins, so they cluster; the
  // layer draws the colored dot / thumbnail-ring and the id+name tooltip.
  const twinEntries = useMemo<ContextTwinEntry[]>(
    () =>
      (twins ?? []).map((t) => ({
        twinId: t.twinId,
        displayName: t.label ?? null,
        modelName: null,
        position: { latitude: t.lat, longitude: t.lon },
        thumbnailUrl: t.thumbnailUrl,
        locationMode: 'Static' as const,
        color: t.color,
      })),
    [twins],
  );

  return (
    <div className={'oe-map' + (placing ? ' placing' : '')} style={{ position: 'relative' }}>
      <TurboTwinMap layer={basemap} center={center} zoom={stops.length ? 17 : 2} maxZoom={MAX_ZOOM}>
        <FitBounds stops={stops} />
        <ClickToPlace onPlace={onPlace} />
        {geo && <GeoOverlayLayer geo={geo} placing={placing} />}
        {twins && twins.length > 0 && (
          <ClusteredMarkersLayer entries={twinEntries} colorOf={twinColor} interactive={!placing} />
        )}
        {movers && movers.length > 0 && (
          <MoversLayer movers={moverEntries} colorOf={moverColor} interactive={!placing} />
        )}
        {path.length > 1 && (
          <Polyline positions={path} pathOptions={{ color: '#e8a33d', weight: 2, opacity: 0.8, dashArray: '6 6' }} />
        )}
        {commandGeo && (
          <Marker
            position={[commandGeo.lat, commandGeo.lon]}
            icon={COMMAND_GEO_ICON}
            interactive={false}
          />
        )}
        {stops.map((s) => (
          <Marker
            key={s.stepId}
            position={[s.lat, s.lon]}
            icon={pinIcon(String(s.index + 1), s.stepId === selectedStepId)}
            draggable={!!onMove && s.stepId === selectedStepId}
            eventHandlers={{
              click: () => onSelect(s.stepId),
              dragend: (e) => {
                if (onMove) {
                  const ll = (e.target as L.Marker).getLatLng();
                  onMove(s.stepId, ll.lat, ll.lng);
                }
              },
            }}
          >
            {/* Hover readout: name + the stop's elevation (sampled from the
                terrain DEM on placement) and its coordinates. */}
            <LeafletTooltip direction="top" offset={[0, -30]} opacity={1}>
              <div style={{ fontWeight: 600 }}>{s.name || `Stop ${s.index + 1}`}</div>
              <div>Elevation {fmtElevation(s.el)}</div>
              <div style={{ opacity: 0.7, fontVariantNumeric: 'tabular-nums' }}>
                {s.lat.toFixed(6)}, {s.lon.toFixed(6)}
              </div>
            </LeafletTooltip>
          </Marker>
        ))}
      </TurboTwinMap>
    </div>
  );
}
